import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { CreditCard, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PLANS = ["Trial", "Standard", "Professional", "Enterprise"];
const STATUSES = ["active", "past_due", "cancelled", "suspended"];

export default function Subscriptions() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [plan, setPlan] = useState("Trial");
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/platform/subscriptions").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openEdit = (o) => { setEditing(o); setPlan(o.subscription_plan || "Trial"); setStatus(o.subscription_status || "active"); setOpen(true); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/platform/organisations/${editing.id}/subscription`, { subscription_plan: plan, subscription_status: status }); toast.success("Subscription updated"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="subscriptions-page">
      <PageHeader title="Subscriptions" subtitle="SaaS plans and billing status for each organisation." />
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Organisation</TableHead><TableHead>Facilities</TableHead><TableHead>Plan</TableHead><TableHead>Billing status</TableHead><TableHead>Since</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.id} className="hover:bg-slate-50" data-testid={`sub-row-${o.id}`}>
                  <TableCell className="font-medium text-slate-800">{o.name}</TableCell>
                  <TableCell className="tabular-nums">{o.facilities_count}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-sm text-slate-700"><CreditCard className="h-3.5 w-3.5 text-slate-400" />{o.subscription_plan}</span></TableCell>
                  <TableCell><StatusBadge status={o.subscription_status === "active" ? "active" : o.subscription_status === "suspended" ? "suspended" : "provisioning"} label={o.subscription_status} /></TableCell>
                  <TableCell className="text-xs text-slate-400 font-mono">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid={`sub-edit-${o.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Manage</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="subscription-dialog">
          <DialogHeader><DialogTitle>Manage Subscription — {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Plan</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="sub-plan-select">{PLANS.map((p) => <option key={p}>{p}</option>)}</select>
            </div>
            <div className="space-y-1.5"><Label>Billing status</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sub-status-select">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="sub-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
